import { useEffect, useState } from "react";
import { Edit } from "react-feather";
import clsx from "clsx";
import { PublicKey } from "@solana/web3.js";
import * as beet from "@metaplex-foundation/beet";
import { useConnection } from "@solana/wallet-adapter-react";
import { BN } from "bn.js";
import CustomSelect, { ListItemsProps } from "@axvdex/components/form-element/CustomSelect";
import useLanguage from "@axvdex/hooks/useLanguage";
import * as nicknameGen from "@axvdex/solana/nicknameAPI/js/src/generated";
import {
  SOL_PUBLIC_KEY,
  requestUpdateNicknameSolana,
  useNicknameMap,
  useUserBalances,
} from "@axvdex/hooks/solana/useNickname";
import { useAppDispatch, useAppSelector } from "@axvdex/state";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import styles from "@axvdex/styles/MyAssetsManageTokensModal.module.scss";
import {
  askSolanaActionFee,
  displayToken,
  setupSolanaFees,
  typeNicknameFiltered,
} from "@axvdex/components/modals/UserSettingsModal";
import Button from "@axvdex/components/common/Button";
import CustomModal from "@axvdex/components/common/CustomModal";
import CustomInput from "@axvdex/components/form-element/CustomInput";
import CustomLoader from "@axvdex/components/common/CustomLoader";
import { sendToast } from "@axvdex/state/wallet/walletSlice";

type SaleNicknameTargetModalProps = {
  targetAddress: string;
  currentNickname: string;
  earlySuccessSetNickname?: (targetPublicKey: PublicKey, nickname: string) => void;
};

function SaleNicknameTargetModal({ targetAddress, earlySuccessSetNickname, currentNickname }: SaleNicknameTargetModalProps) {
  const { i18 } = useLanguage();
  const { connection } = useConnection();
  const dispatch = useAppDispatch();

  const startingFees = { actionFees: new Map<nicknameGen.ActionType, nicknameGen.TokenFees>([]) };

  const [isOpen, setIsOpen] = useState(false);
  const [isWillingToPayForAction, setIsWillingToPayForAction] = useState(false);
  const [nickname, setNickname] = useState(currentNickname);

  const { nicknames } = useNicknameMap();
  const [loading, setLoading] = useState(false);
  const [tokenCosts, setTokenCosts] = useState(startingFees);
  const [fee, setFee] = useState<Partial<beet.bignum>>(0);
  const [selectedMint, setSelectedMint] = useState(SOL_PUBLIC_KEY);
  const walletInfo = useAppSelector(selectWalletInfo);
  const balances = useUserBalances();

  const payment: nicknameGen.Payment = {
    mintAddress: selectedMint,
    amount: new BN(fee.valueOf().toString()),
  };

  //const allTokenOptions = ["SOL", "AXVS"]
  const allTokenOptions: ListItemsProps[] = [
    { label: "SOL", value: SOL_PUBLIC_KEY.toString() },
    // { label: "AXVS", value: SOL_PUBLIC_KEY.toString() }, TODO: add AXVS
  ];

  const submitNicknameForm = async () => {
    if (!walletInfo.isConnected) {
      dispatch(
        sendToast({ type: "tx-fail", info: { msg: "Wallet not connected!", toastID: "" + new Date().getTime() } })
      );
      return;
    }
    setLoading(true);
    const userWalletAddress = new PublicKey(walletInfo.walletAddress);
    const targetAddressPubKey = new PublicKey(targetAddress);
    if (earlySuccessSetNickname) earlySuccessSetNickname(targetAddressPubKey, nickname);
    await requestUpdateNicknameSolana(connection, targetAddressPubKey, nickname, payment, userWalletAddress);
    setLoading(false);
    setIsWillingToPayForAction(false);
    setIsOpen(false);
  };

  useEffect(() => {
    setupSolanaFees(connection, setTokenCosts, setFee, nicknameGen.ActionType.OverrideOtherNickname, SOL_PUBLIC_KEY);
    const currentNickname = nicknames.get(targetAddress);
    if (currentNickname) {
      setNickname(currentNickname.nickname);
    }
  }, []);

  useEffect(() => {
    const newFee: Partial<beet.bignum> = askSolanaActionFee(
      tokenCosts,
      nicknameGen.ActionType.OverrideOtherNickname,
      selectedMint
    );
    if (newFee) {
      setFee(newFee);
    } else {
      console.error("fee is undefined");
    }
  }, [selectedMint, tokenCosts]);

  return (
    <>
      <Button
        title="Edit Nickname"
        btnColor="dark-medium"
        style={{ padding: ".25em", paddingLeft: "1em", paddingRight: "1em" }}
        icon={<Edit style={{ color: "white" }} />}
        onClick={() => setIsOpen(true)}
      />
      <CustomModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        extraClassName={clsx(styles.permitAuthenticationModal, "permitAuthenticationModal")}
      >
        <section className="sectionModalHeader">
          <h2 className="h2">{i18("Update Nickname", "userWallet.settings.title")}</h2>
        </section>

        <section className="sectionModalContent withGradientBorderBottom">
          <div className="inlineFlexbox" style={{ width: "100%", placeContent: "space-between" }}>
            <CustomInput
              name="nickname"
              labelText={i18(
                "Nickname (A-Z, 0-9, _ and - with 20 characters max.)",
                "userWallet.settings.nicknameDetail"
              )}
              title={i18(`Nickname`, "userWallet.settings.nickname")}
              value={nickname}
              onChange={e => typeNicknameFiltered(setNickname, setIsWillingToPayForAction, e)}
            />
          </div>
        </section>

        <div>
          <CustomSelect
            name="select_payment_token"
            labelText="Token Payment"
            hiddenLabel={true}
            placeholder="Payment Token - SOL"
            items={allTokenOptions}
            onChange={(option: ListItemsProps) => {
              const asPK = new PublicKey(option.value);
              setSelectedMint(asPK);
            }}
            required
          />
        </div>

        <div className="inlineFlexbox" style={{ width: "100%" }}>
          <div className="inlineFlexbox" style={{ width: "65%" }}>
            <input
              type="checkbox"
              checked={isWillingToPayForAction}
              onChange={e => setIsWillingToPayForAction(e.target.checked)}
            />
            <span>Pay to update this nickname</span>
          </div>

          <div style={{ marginLeft: "auto", textAlign: "center", marginTop: 16 }}>
            {loading ? (
              <CustomLoader size="xs" />
            ) : (
              <Button
                btnColor="gradient"
                title="Update"
                style={{ marginBottom: ".5em" }}
                onClick={() => submitNicknameForm()}
                disabled={!isWillingToPayForAction}
              >
                <div>Update:</div>
                <br />
                <div>
                  <small>
                    {fee.toString()} {displayToken(selectedMint)}
                  </small>
                </div>
              </Button>
            )}
            <br />
            <div>Balance: {balances.get(selectedMint.toString()) ? balances.get(selectedMint.toString()) : "---"}</div>
          </div>
        </div>
      </CustomModal>
    </>
  );
}

export default SaleNicknameTargetModal;
